import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Search, Globe, Building2, MoreHorizontal, Eye, CheckCircle, XCircle, Download,
  Shield, Zap, Users, IndianRupee, CalendarCheck, Mail, Phone, MapPin,
  Trophy, Crown, Clock, Ban, Trash2, ArrowUpCircle, Sparkles, Loader2, Key, BadgeCheck, AlertTriangle, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { AcademyDrilldown, fmtDate } from './AcademyDrilldown';

const STAT_TABS = [
  { key: 'students', label: 'Students', icon: Users, color: 'text-primary', value: (a) => a.student_count || 0 },
  { key: 'employees', label: 'Employees', icon: Building2, color: 'text-blue-600', value: (a) => a.employee_count || 0 },
  { key: 'revenue', label: 'Revenue', icon: IndianRupee, color: 'text-emerald-600', value: (a) => `₹${(a.total_revenue || 0).toLocaleString('en-IN')}` },
  { key: 'sports', label: 'Sports', icon: Trophy, color: 'text-amber-600', value: (a) => a.sports_count ?? (a.sport_types || []).length },
];

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const NEW_SIGNUP_DAYS = 7;
const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};
const daysLabel = (n) => (n === 0 ? 'Today' : n === 1 ? '1 day ago' : `${n} days ago`);

// ========== STATUS CONFIG ==========
const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800', dot: 'bg-emerald-500' },
  trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800', dot: 'bg-blue-500' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800', dot: 'bg-amber-500' },
  inactive: { label: 'Inactive', color: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800', dot: 'bg-red-500' },
  free_trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800', dot: 'bg-blue-500' },
};

const getAcademyStatus = (a) => {
  const sub = a.subscription || {};
  const plan = sub.plan || 'free_trial';
  const status = sub.status || (plan === 'free_trial' ? 'trial' : 'active');
  return { status, plan, config: statusConfig[status] || statusConfig.active };
};

const isPlanBased = (a) => {
  const { plan, status } = getAcademyStatus(a);
  return (plan === 'pro' || plan === 'enterprise' || plan === 'starter_paid') && status !== 'inactive';
};

const isOnTrial = (a) => {
  const { plan, status } = getAcademyStatus(a);
  return (plan === 'free_trial' || plan === 'free' || plan === 'starter') && status !== 'inactive';
};

const isDeactivated = (a) => {
  const { status } = getAcademyStatus(a);
  return status === 'inactive';
};

// ========== KPI CARD COMPONENT ==========
function KPIFilterCard({ icon: Icon, title, count, total, revenue, isActive, onClick, gradient, iconBg, testId }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`group relative w-full text-left rounded-xl border-2 p-4 transition-all duration-200 overflow-hidden
        ${isActive
          ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02]'
          : 'border-transparent hover:border-border hover:shadow-md hover:-translate-y-0.5'
        }`}
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 opacity-[0.06] ${gradient}`} />

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconBg}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          {isActive && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary text-primary-foreground">
              Active Filter
            </span>
          )}
        </div>

        <p className="text-xs font-medium text-muted-foreground mb-1">{title}</p>

        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-display font-bold tabular-nums text-foreground">{count}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{pct}% of total</p>
          </div>
          {revenue !== undefined && revenue > 0 && (
            <div className="text-right">
              <p className="text-sm font-semibold tabular-nums text-foreground">₹{revenue.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">/month</p>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${iconBg}`}
            style={{ width: `${Math.max(pct, 3)}%` }}
          />
        </div>
      </div>
    </button>
  );
}

// ========== MAIN COMPONENT ==========
export default function AdminAcademies() {
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewAcademy, setViewAcademy] = useState(null);
  const [detailTab, setDetailTab] = useState(null);
  const [changePlanDialog, setChangePlanDialog] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [sendingWelcome, setSendingWelcome] = useState(null);
  const [activating, setActivating] = useState(null);
  const [showActivateConfirm, setShowActivateConfirm] = useState(null);
  // Phase 68 — Super Admin manual tools
  const [verifyingManually, setVerifyingManually] = useState(null);
  const [forceResetTarget, setForceResetTarget] = useState(null);
  const [forceResetPassword, setForceResetPassword] = useState('');
  const [forceResetConfirm, setForceResetConfirm] = useState('');
  const [forceResetting, setForceResetting] = useState(false);
  // Phase 255g — "Force Unstick PAYMENT_PENDING" row action
  const [unstickTarget, setUnstickTarget] = useState(null);
  const [unsticking, setUnsticking] = useState(false);

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/admin/academies'); setAcademies(r.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setDetailTab(null); }, [viewAcademy?.id]);

  // ========== COMPUTED STATS ==========
  const stats = useMemo(() => {
    const all = academies.length;
    const planBased = academies.filter(isPlanBased);
    const onTrial = academies.filter(isOnTrial);
    const deactivated = academies.filter(isDeactivated);
    const planRevenue = planBased.reduce((sum, a) => {
      const plan = a.subscription?.plan || '';
      if (plan === 'enterprise') return sum + 7999;
      if (plan === 'pro') return sum + 999;
      return sum + (a.subscription?.monthly_price || 0);
    }, 0);

    return {
      all, planBased: planBased.length, onTrial: onTrial.length,
      deactivated: deactivated.length, planRevenue
    };
  }, [academies]);

  // ========== FILTERED LIST ==========
  const filtered = useMemo(() => {
    let list = academies;

    // Apply card filter
    if (activeFilter === 'plan') list = list.filter(isPlanBased);
    else if (activeFilter === 'trial') list = list.filter(isOnTrial);
    else if (activeFilter === 'inactive') list = list.filter(isDeactivated);

    // Apply search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.city?.toLowerCase().includes(q) ||
        a.custom_id?.toLowerCase().includes(q) ||
        a.phone?.includes(q)
      );
    }

    return list;
  }, [academies, activeFilter, search]);

  // ========== ACTIONS ==========
  const handleToggleStatus = (a) => {
    const current = a.subscription?.status || 'active';
    const newStatus = current === 'active' || current === 'trial' ? 'inactive' : 'active';
    toast.success(`${a.name} ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    setAcademies(prev => prev.map(ac => ac.id === a.id ? { ...ac, subscription: { ...ac.subscription, status: newStatus } } : ac));
  };

  const handleChangePlan = (a) => {
    setChangePlanDialog(a);
    setNewPlan(a.subscription?.plan || 'free_trial');
  };

  const handleSavePlan = () => {
    if (!changePlanDialog) return;
    toast.success(`${changePlanDialog.name} plan changed to ${newPlan}`);
    setAcademies(prev => prev.map(ac => ac.id === changePlanDialog.id
      ? { ...ac, subscription: { ...ac.subscription, plan: newPlan, status: newPlan === 'free_trial' ? 'trial' : 'active' } }
      : ac
    ));
    setChangePlanDialog(null);
  };

  const handleDeleteAcademy = async (a) => {
    if (!window.confirm(`PERMANENTLY DELETE "${a.name}"?\n\nThis will remove the academy AND all linked users, students, employees, fees, attendance, events, and all other data.\n\nThis action cannot be undone.`)) return;
    try {
      const res = await api.delete(`/super-admin/academies/${a.id}`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleResendWelcome = async (a) => {
    setSendingWelcome(a.id);
    try {
      const res = await api.post(`/super-admin/academies/${a.id}/resend-welcome`);
      if (res.data.success) { toast.success(res.data.message); }
      else { toast.error(res.data.message || 'Failed to send'); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send welcome email'); }
    finally { setSendingWelcome(null); }
  };

  const handleActivateAcademy = async (a, plan = 'pro') => {
    setActivating(a.id);
    try {
      const res = await api.post(`/super-admin/activate-academy/${a.id}`, { plan });
      toast.success(res.data.message);
      setShowActivateConfirm(null);
      setViewAcademy(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to activate'); }
    finally { setActivating(null); }
  };

  // Phase 255g — Force unstick PAYMENT_PENDING. Clears the stuck banner +
  // grace flags AND marks any pending invoices as paid so the cron doesn't
  // re-flag the academy on its next sweep.
  const handleUnstickPaymentPending = async () => {
    if (!unstickTarget) return;
    setUnsticking(true);
    try {
      const res = await api.post(`/super-admin/academies/${unstickTarget.id}/unstick-payment-pending`);
      toast.success(res.data.message || 'PAYMENT_PENDING cleared');
      setUnstickTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unstick PAYMENT_PENDING');
    } finally {
      setUnsticking(false);
    }
  };

  // Phase 68 — Super admin "Verify Manually" — flips an unverified academy to TRIAL state instantly
  const handleVerifyManually = async (a) => {    setVerifyingManually(a.id);
    try {
      const res = await api.post(`/super-admin/academies/${a.id}/verify-manually`);
      toast.success(res.data.message || 'Academy verified');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify academy');
    } finally {
      setVerifyingManually(null);
    }
  };

  // Phase 68 — Super admin "Force Reset Password" — sets a new owner password + emails it
  const handleForceResetPassword = async () => {
    if (!forceResetTarget) return;
    if (forceResetPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (forceResetPassword !== forceResetConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    setForceResetting(true);
    try {
      const res = await api.post(`/super-admin/academies/${forceResetTarget.id}/force-reset-password`, {
        new_password: forceResetPassword
      });
      toast.success(res.data.message || 'Password reset and emailed to owner');
      setForceResetTarget(null);
      setForceResetPassword('');
      setForceResetConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForceResetting(false);
    }
  };

  const handleExport = () => {
    const rows = [['ID','Name','City','Phone','Sports','Sports Count','Students','Employees','Revenue','Plan','Status','Registered On','Pro Activated On'],
      ...academies.map(a => [a.custom_id||'', a.name||'', a.city||'', a.phone||'', (a.sport_types||[]).join(';'), a.sports_count ?? (a.sport_types||[]).length, a.student_count||0, a.employee_count||0, a.total_revenue||0, a.subscription?.plan||'free_trial', getAcademyStatus(a).status, (a.created_at||'').slice(0,10), (a.pro_activated_at||'').slice(0,10)])
    ];
    const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = 'academies.csv'; link.click();
    toast.success('Academies exported');
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(prev => prev === filter ? 'all' : filter);
  };

  // ========== LOADING ==========
  if (loading) return (
    <div className="space-y-6" data-testid="admin-academies-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-36 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  // ========== RENDER ==========
  return (
    <div className="space-y-6" data-testid="admin-academies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Academies</h1>
          <p className="text-sm text-muted-foreground">{academies.length} registered academies</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-button">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* 4 KPI Filter Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="kpi-filter-cards">
        <KPIFilterCard
          icon={Trophy}
          title="All Academies"
          count={stats.all}
          total={stats.all}
          isActive={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
          gradient="bg-gradient-to-br from-emerald-400 to-emerald-600"
          iconBg="bg-emerald-600"
          testId="kpi-card-all"
        />
        <KPIFilterCard
          icon={Crown}
          title="Plan Based"
          count={stats.planBased}
          total={stats.all}
          revenue={stats.planRevenue}
          isActive={activeFilter === 'plan'}
          onClick={() => handleFilterClick('plan')}
          gradient="bg-gradient-to-br from-amber-400 to-amber-600"
          iconBg="bg-amber-600"
          testId="kpi-card-plan"
        />
        <KPIFilterCard
          icon={Clock}
          title="On Trial"
          count={stats.onTrial}
          total={stats.all}
          isActive={activeFilter === 'trial'}
          onClick={() => handleFilterClick('trial')}
          gradient="bg-gradient-to-br from-blue-400 to-blue-600"
          iconBg="bg-blue-600"
          testId="kpi-card-trial"
        />
        <KPIFilterCard
          icon={Ban}
          title="Deactivated"
          count={stats.deactivated}
          total={stats.all}
          isActive={activeFilter === 'inactive'}
          onClick={() => handleFilterClick('inactive')}
          gradient="bg-gradient-to-br from-red-400 to-red-600"
          iconBg="bg-red-600"
          testId="kpi-card-deactivated"
        />
      </div>

      {/* Filtered Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm">
                {activeFilter === 'all' ? 'All Academies' :
                 activeFilter === 'plan' ? 'Plan Based Academies' :
                 activeFilter === 'trial' ? 'On Trial Academies' :
                 'Deactivated Academies'}
              </CardTitle>
              <Badge variant="secondary" className="tabular-nums text-xs">{filtered.length}</Badge>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, city, ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="academies-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">ID</TableHead>
                  <TableHead>Academy</TableHead>
                  <TableHead className="hidden md:table-cell">City</TableHead>
                  <TableHead className="hidden lg:table-cell">Registered</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2">
                        {activeFilter === 'inactive' ? (
                          <>
                            <Ban className="h-10 w-10 text-muted-foreground/40" />
                            <p className="text-sm font-medium text-muted-foreground">No deactivated academies</p>
                            <p className="text-xs text-muted-foreground">All academies are currently active</p>
                          </>
                        ) : activeFilter === 'plan' ? (
                          <>
                            <Crown className="h-10 w-10 text-muted-foreground/40" />
                            <p className="text-sm font-medium text-muted-foreground">No plan-based academies</p>
                            <p className="text-xs text-muted-foreground">No academies on paid plans yet</p>
                          </>
                        ) : (
                          <>
                            <Building2 className="h-10 w-10 text-muted-foreground/40" />
                            <p className="text-sm font-medium text-muted-foreground">No academies found</p>
                            <p className="text-xs text-muted-foreground">Try adjusting your search or filter</p>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(a => {
                  const { config: st } = getAcademyStatus(a);
                  const plan = a.subscription?.plan || 'free_trial';
                  const planLabel = plan === 'pro' ? '₹999' : plan === 'enterprise' ? '₹7,999' : plan === 'starter_paid' ? '₹499' : 'Free';
                  const age = daysSince(a.created_at);
                  const isNew = age !== null && age <= NEW_SIGNUP_DAYS;

                  return (
                    <TableRow key={a.id} data-testid={`academy-row-${a.id}`} className={`group ${isNew ? 'bg-emerald-50/40 dark:bg-emerald-950/10' : ''}`}>
                      <TableCell>
                        <span className="text-xs font-mono text-muted-foreground">{a.custom_id || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{a.name}</p>
                          <p className="text-xs text-muted-foreground">{a.phone || (a.sport_types || []).join(', ') || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">{a.city || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell" data-testid={`academy-registered-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-sm tabular-nums">{fmtDate(a.created_at)}</p>
                            {age !== null && <p className="text-[11px] text-muted-foreground">{daysLabel(age)}</p>}
                          </div>
                          {isNew && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600 text-white" data-testid={`academy-new-badge-${a.id}`}>
                              <Sparkles className="h-2.5 w-2.5" /> New
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="tabular-nums font-medium">{a.student_count || 0}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize tabular-nums">
                          {planLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                          {st.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 transition-opacity duration-150" data-testid={`academy-actions-${a.custom_id || a.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => setViewAcademy(a)} data-testid="action-view-details">
                              <Eye className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangePlan(a)} data-testid="action-change-plan">
                              <ArrowUpCircle className="h-4 w-4 mr-2" /> Change Plan
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleStatus(a)} data-testid="action-toggle-status">
                              {(a.subscription?.status || 'active') === 'inactive'
                                ? <><Zap className="h-4 w-4 mr-2" /> Activate</>
                                : <><XCircle className="h-4 w-4 mr-2" /> Deactivate</>
                              }
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResendWelcome(a)} disabled={sendingWelcome === a.id}>
                              {sendingWelcome === a.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                              {sendingWelcome === a.id ? 'Sending...' : 'Re-send Setup Guide'}
                            </DropdownMenuItem>
                            {/* Phase 68 — Super Admin manual tools */}
                            {a.subscription_status === 'PENDING_VERIFICATION' && (
                              <DropdownMenuItem
                                onClick={() => handleVerifyManually(a)}
                                disabled={verifyingManually === a.id}
                                className="text-emerald-600 focus:text-emerald-600"
                                data-testid="action-verify-manually"
                              >
                                {verifyingManually === a.id
                                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  : <BadgeCheck className="h-4 w-4 mr-2" />}
                                {verifyingManually === a.id ? 'Verifying…' : 'Verify Manually'}
                              </DropdownMenuItem>
                            )}
                            {/* Phase 255g — Force Unstick PAYMENT_PENDING. Only surfaces
                                when the academy is actually stuck in PAYMENT_PENDING. */}
                            {a.subscription_status === 'PAYMENT_PENDING' && (
                              <DropdownMenuItem
                                onClick={() => setUnstickTarget(a)}
                                className="text-amber-700 focus:text-amber-700"
                                data-testid="action-unstick-payment-pending"
                              >
                                <AlertTriangle className="h-4 w-4 mr-2" /> Force Unstick PAYMENT_PENDING
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => { setForceResetTarget(a); setForceResetPassword(''); setForceResetConfirm(''); }}
                              className="text-amber-600 focus:text-amber-600"
                              data-testid="action-force-reset-password"
                            >
                              <Key className="h-4 w-4 mr-2" /> Force Reset Password
                            </DropdownMenuItem>
                            {a.subscription_status !== 'ACTIVE' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setShowActivateConfirm(a)} className="text-emerald-600 focus:text-emerald-600" data-testid="action-activate">
                                  <CheckCircle className="h-4 w-4 mr-2" /> Activate Academy
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteAcademy(a)} className="text-destructive focus:text-destructive" data-testid="action-delete-academy">
                              <Trash2 className="h-4 w-4 mr-2" /> Delete Academy
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Details Modal */}
      <Dialog open={!!viewAcademy} onOpenChange={() => setViewAcademy(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="academy-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" /> {viewAcademy?.name}
            </DialogTitle>
          </DialogHeader>
          {viewAcademy && (
            <div className="space-y-4 min-w-0">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <Badge variant="outline" className="font-mono text-xs" data-testid="details-academy-id">{viewAcademy.custom_id || '—'}</Badge>
                {(() => {
                  const { config: st } = getAcademyStatus(viewAcademy);
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                    </span>
                  );
                })()}
                <Badge variant="outline" className="text-xs capitalize">
                  {(viewAcademy.subscription?.plan || 'free_trial').replace('_', ' ')} plan
                </Badge>
                <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="details-registered-chip">
                  <CalendarCheck className="h-3.5 w-3.5" /> Registered {fmtDate(viewAcademy.created_at)}
                </span>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="details-stat-tabs">
                {STAT_TABS.map(({ key, label, icon: Icon, color, value }) => {
                  const active = detailTab === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setDetailTab(active ? null : key)}
                      data-testid={`details-tab-${key}`}
                      aria-pressed={active}
                      className={`group relative rounded-xl border p-3 text-center transition-all duration-200
                        ${active ? 'border-primary bg-primary/5 shadow-md shadow-primary/10' : 'border-border bg-card hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5'}`}
                    >
                      <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
                      <p className="text-lg font-display font-bold tabular-nums">{value(viewAcademy)}</p>
                      <p className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                        {label}
                        <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${active ? 'rotate-90 text-primary' : 'opacity-0 group-hover:opacity-100'}`} />
                      </p>
                    </button>
                  );
                })}
              </div>

              <Separator />

              {detailTab ? (
                <AcademyDrilldown academy={viewAcademy} tab={detailTab} onBack={() => setDetailTab(null)} />
              ) : (
                <div className="grid grid-cols-2 gap-4" data-testid="details-overview">
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Academy Info</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /><span>{viewAcademy.name}</span></div>
                      {viewAcademy.owner_email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /><a href={`mailto:${viewAcademy.owner_email}`} className="text-primary hover:underline">{viewAcademy.owner_email}</a></div>}
                      {viewAcademy.city && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /><span>{viewAcademy.city}</span></div>}
                      {viewAcademy.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><span>{viewAcademy.phone}</span></div>}
                      <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-muted-foreground" /><span>{(viewAcademy.sport_types||[]).join(', ') || 'General'}</span></div>
                      <div className="flex items-center gap-2"><CalendarCheck className="h-3.5 w-3.5 text-muted-foreground" /><span>Registered on <span className="font-medium" data-testid="details-registered-on">{fmtDate(viewAcademy.created_at)}</span></span></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Subscription</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-medium capitalize">{(viewAcademy.subscription?.plan || 'free_trial').replace('_', ' ')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="font-medium capitalize">{viewAcademy.subscription?.status || 'Active'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Registered On</span><span className="font-medium tabular-nums">{fmtDate(viewAcademy.created_at)}</span></div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pro Activated On</span>
                        <span className="font-medium tabular-nums" data-testid="details-pro-activated-on" title={viewAcademy.pro_activated_estimated ? 'Estimated from the billing cycle (activation stamp missing on legacy record)' : undefined}>
                          {viewAcademy.pro_activated_at
                            ? <>{viewAcademy.pro_activated_estimated && <span className="text-muted-foreground mr-0.5">≈</span>}{fmtDate(viewAcademy.pro_activated_at)}</>
                            : <span className="text-muted-foreground italic font-normal">Not activated</span>}
                        </span>
                      </div>
                      {viewAcademy.subscription?.trial_end_date && !viewAcademy.pro_activated_at && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Trial Ends</span><span className="font-medium tabular-nums">{fmtDate(viewAcademy.subscription.trial_end_date)}</span></div>
                      )}
                      {viewAcademy.subscription?.next_billing_date && viewAcademy.pro_activated_at && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Next Billing</span><span className="font-medium tabular-nums">{fmtDate(viewAcademy.subscription.next_billing_date)}</span></div>
                      )}
                      {viewAcademy.subscription?.expires && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Expires</span><span className="font-medium tabular-nums">{fmtDate(viewAcademy.subscription.expires)}</span></div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              <div className="flex gap-2 flex-wrap">
                {viewAcademy?.subscription_status !== 'ACTIVE' && (
                  <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowActivateConfirm(viewAcademy)} disabled={activating === viewAcademy?.id}>
                    {activating === viewAcademy?.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    {activating === viewAcademy?.id ? 'Activating...' : 'Activate Pro'}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { handleChangePlan(viewAcademy); setViewAcademy(null); }}>
                  <ArrowUpCircle className="h-4 w-4 mr-2" /> Change Plan
                </Button>
                <Button size="sm" variant="outline" data-testid="resend-setup-guide-btn" onClick={() => { handleResendWelcome(viewAcademy); }} disabled={sendingWelcome === viewAcademy?.id}>
                  {sendingWelcome === viewAcademy?.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  {sendingWelcome === viewAcademy?.id ? 'Sending...' : 'Setup Guide'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Plan Modal */}
      <Dialog open={!!changePlanDialog} onOpenChange={() => setChangePlanDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-primary" /> Change Plan
            </DialogTitle>
          </DialogHeader>
          {changePlanDialog && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm font-medium">{changePlanDialog.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Current plan: <span className="font-medium capitalize">{changePlanDialog.subscription?.plan || 'free_trial'}</span>
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">New Plan</Label>
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger data-testid="change-plan-select">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free_trial">Free Trial (14 days)</SelectItem>
                    <SelectItem value="pro">Pro — ₹999/month</SelectItem>
                    <SelectItem value="enterprise">Enterprise — ₹7,999/month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setChangePlanDialog(null)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSavePlan} data-testid="save-plan-button">
                  <CheckCircle className="h-4 w-4 mr-1" /> Save Plan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 255g — Force Unstick PAYMENT_PENDING confirmation Dialog */}
      <Dialog open={!!unstickTarget} onOpenChange={(o) => { if (!o) setUnstickTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="unstick-payment-pending-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Force Unstick PAYMENT_PENDING
            </DialogTitle>
          </DialogHeader>
          {unstickTarget && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="font-medium text-sm">{unstickTarget.name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Current status: <Badge variant="outline" className="ml-1">{unstickTarget.subscription_status || 'UNKNOWN'}</Badge>
                </p>
                {unstickTarget.owner_email && (
                  <p className="text-xs text-muted-foreground mt-1">Owner: <span className="font-mono">{unstickTarget.owner_email}</span></p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-950/40 border text-xs text-slate-700 dark:text-slate-300 space-y-1">
                <p className="font-semibold">This action will:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Flip <code className="text-[10px] bg-slate-200 dark:bg-slate-800 px-1 rounded">subscription_status</code> → <strong>ACTIVE</strong></li>
                  <li>Clear all grace-period flags + invoice due dates</li>
                  <li>Mark any pending invoices as <strong>paid</strong></li>
                  <li>Tell the owner to log out + back in to refresh their banner state</li>
                </ul>
              </div>
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 text-xs text-red-800 dark:text-red-300">
                <strong>Warning:</strong> Billing dates (<code className="text-[10px]">next_billing_date</code>, <code className="text-[10px]">expires</code>) are NOT touched. The next billing cron will still issue a fresh invoice on schedule. Use only when payment reconciliation didn't fan-out properly.
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setUnstickTarget(null)} disabled={unsticking}>Cancel</Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleUnstickPaymentPending}
                  disabled={unsticking}
                  data-testid="unstick-payment-pending-confirm-btn"
                >
                  {unsticking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                  {unsticking ? 'Unsticking…' : 'Confirm Force Unstick'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 68 — Force Reset Password Dialog */}
      <Dialog open={!!forceResetTarget} onOpenChange={(o) => { if (!o) { setForceResetTarget(null); setForceResetPassword(''); setForceResetConfirm(''); } }}>
        <DialogContent className="max-w-md" data-testid="force-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-amber-600" /> Force Reset Owner Password</DialogTitle>
          </DialogHeader>
          {forceResetTarget && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="font-medium text-sm">{forceResetTarget.name}</p>
                {forceResetTarget.owner_email && <p className="text-xs text-muted-foreground mt-1">Owner: <span className="font-mono">{forceResetTarget.owner_email}</span></p>}
              </div>
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 text-xs text-red-800 dark:text-red-300">
                <strong>Warning:</strong> The new password will be emailed to the owner in plaintext. All their active sessions will be invalidated immediately. Use only for support recovery.
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">New Password (min 8 chars)</Label>
                <Input type="password" value={forceResetPassword} onChange={(e) => setForceResetPassword(e.target.value)} placeholder="••••••••" data-testid="force-reset-password-input" autoFocus />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confirm New Password</Label>
                <Input type="password" value={forceResetConfirm} onChange={(e) => setForceResetConfirm(e.target.value)} placeholder="••••••••" data-testid="force-reset-confirm-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setForceResetTarget(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleForceResetPassword}
                  disabled={forceResetting || forceResetPassword.length < 8 || forceResetPassword !== forceResetConfirm}
                  data-testid="force-reset-confirm-btn"
                >
                  {forceResetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Key className="h-4 w-4 mr-1" />}
                  {forceResetting ? 'Resetting…' : 'Reset & Email Owner'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activate Academy Confirmation Dialog */}
      <Dialog open={!!showActivateConfirm} onOpenChange={() => setShowActivateConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /> Activate Academy</DialogTitle>
          </DialogHeader>
          {showActivateConfirm && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                <p className="font-medium text-sm">{showActivateConfirm.name}</p>
                <p className="text-xs text-muted-foreground mt-1">Current status: <Badge variant="outline" className="ml-1">{showActivateConfirm.subscription_status || 'TRIAL'}</Badge></p>
                {showActivateConfirm.owner_email && <p className="text-xs text-muted-foreground mt-1">Owner: {showActivateConfirm.owner_email}</p>}
              </div>
              <p className="text-sm text-muted-foreground">This will activate the academy on the <strong>Pro plan (₹999/month)</strong> for 30 days and send an activation email to the owner.</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowActivateConfirm(null)}>Cancel</Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleActivateAcademy(showActivateConfirm, 'pro')} disabled={activating === showActivateConfirm.id} data-testid="confirm-activate-btn">
                  {activating === showActivateConfirm.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  {activating === showActivateConfirm.id ? 'Activating...' : 'Activate Pro — 30 Days'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
